import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { FlakinessReport } from '@flakiness/flakiness-report';
import { EnvGroup } from '@flakiness/server/common/stats/envGroup.js';
import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { consume, ContextType } from '@lit/context';
import { classMap } from 'lit/directives/class-map.js';
import { contexts } from '../contexts.js';
import { forgeUtils } from '../forgeUtils.js';
import { AppLink, hrefOrClick } from '../linkifier.js';
import { linkStyles } from './cssstyles.js';

@customElement('timeline-name')
export class TimelineName extends LitElement {

  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;
  @property({ attribute: false }) timeline?: WireTypes.JSONEnvGroup;
  @property({ attribute: false }) link?: AppLink;

  override render() {
    if (!this.timeline)
      return nothing;

    const description = EnvGroup.deserialize(this.timeline).description();
    return html`
      <h-box class=content>
        <v-box style="gap: var(--sl-spacing-2x-small);">
          <a class=${classMap({ clickable: !!this.link })} ${hrefOrClick(this.link)}>
            <h-box class=timeline-name>
              <div style="flex: none;">${(description.name ?? '').trim().length ? description.name : html`<span class=noname>[no name]</span>`}</div>
              ${description.metadata ? html`<span>(${description.metadata})</span>` : nothing}
              ${description.system ? html`
                <span class=timeline-system>
                  <sl-icon name=dot></sl-icon>
                  <span>${description.system}</span>
                </span>
              ` : nothing}
              ${description.runtime ? html`
                <span class=timeline-system>
                  <sl-icon name=dot></sl-icon>
                  <span>${description.runtime}</span>
                </span>
              ` : nothing}
            </h-box>
          </a>
          ${description.configPath ? html`
            <a-ext class=configpath href=${forgeUtils.hasSourceUrl(this._project) ? forgeUtils.fileUrl(this._project, 'main' as FlakinessReport.CommitId, description.configPath) : nothing}>${description.configPath}</a-ext>
          ` : nothing}
        </v-box>
      </h-box>
    `;
  }

  static styles = [linkStyles, css`
    :host {
      flex: none;
    }

    .clickable {
      cursor: pointer;
    }

    sl-icon {
      color: var(--sl-color-neutral-500);
    }

    .timeline-name {
      gap: var(--sl-spacing-2x-small);
      display: flex;
      align-items: center;
      font-weight: var(--sl-font-weight-semibold);

      &[selected] {
        background-color: var(--fk-color-highlight);
      }

      & > * {
        text-overflow: ellipsis;
        overflow: hidden;
        min-width: 0;
      }
    }

    .timeline-system {
      display: inline-flex;
      align-items: center;
      gap: var(--sl-spacing-2x-small);
    }

    .noname {
      color: var(--sl-color-neutral-500);
    }

    .configpath {
      color: var(--sl-color-neutral-500);
      font-size: var(--sl-font-size-small);
    }
  `]
}
